import type { ReactNode, ComponentType } from 'react'

// Base component props
interface BaseProps {
  children?: ReactNode
}

// Hero Section
interface HeroProps extends BaseProps {
  title: string
  subtitle?: string
  backgroundImage?: string
  ctaText?: string
  ctaLink?: string
  secondaryCtaText?: string
  secondaryCtaLink?: string
  align?: 'left' | 'center' | 'right'
  overlay?: boolean
  height?: 'small' | 'medium' | 'large' | 'full'
}

function Hero({
  title,
  subtitle,
  backgroundImage,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  align = 'center',
  overlay = true,
  height = 'large',
}: HeroProps) {
  const alignClass = { left: 'text-left items-start', center: 'text-center items-center', right: 'text-right items-end' }[
    align
  ]
  const heightClass = { small: 'py-16', medium: 'py-24', large: 'py-32 md:py-40', full: 'min-h-screen' }[height]

  return (
    <section
      className={'relative flex flex-col justify-center px-4 ' + heightClass}
      style={
        backgroundImage
          ? {
              backgroundImage: 'url(' + backgroundImage + ')',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : { backgroundColor: '#1f2937' }
      }
    >
      {(backgroundImage || !backgroundImage) && overlay && <div className="absolute inset-0 bg-black/50" />}
      <div className={'relative z-10 max-w-4xl mx-auto flex flex-col ' + alignClass}>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">{title}</h1>
        {subtitle && <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl">{subtitle}</p>}
        {(ctaText || secondaryCtaText) && (
          <div className="flex flex-wrap gap-4 justify-center">
            {ctaText && ctaLink && (
              <a
                href={ctaLink}
                className="inline-block bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                {ctaText}
              </a>
            )}
            {secondaryCtaText && secondaryCtaLink && (
              <a
                href={secondaryCtaLink}
                className="inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                {secondaryCtaText}
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

// Text Block
interface TextBlockProps extends BaseProps {
  content: string
  align?: 'left' | 'center' | 'right'
}

function TextBlock({ content, align = 'left' }: TextBlockProps) {
  const alignClass = { left: 'text-left', center: 'text-center', right: 'text-right' }[align]
  return <div className={'prose prose-lg max-w-none ' + alignClass} dangerouslySetInnerHTML={{ __html: content }} />
}

// Image
interface ImageProps extends BaseProps {
  src: string
  alt?: string
  caption?: string
  fullWidth?: boolean
  rounded?: boolean
}

function Image({ src, alt, caption, fullWidth, rounded = true }: ImageProps) {
  return (
    <figure className={'my-8 ' + (fullWidth ? '' : 'max-w-4xl mx-auto')}>
      <img src={src} alt={alt || ''} className={'w-full ' + (rounded ? 'rounded-lg' : '')} />
      {caption && <figcaption className="text-center text-gray-600 mt-2 text-sm">{caption}</figcaption>}
    </figure>
  )
}

// Grid
interface GridProps extends BaseProps {
  columns?: number
  gap?: number
}

function Grid({ columns = 3, gap = 6, children }: GridProps) {
  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: 'repeat(' + Math.min(columns, 4) + ', minmax(0, 1fr))',
        gap: gap * 4 + 'px',
      }}
    >
      {children}
    </div>
  )
}

// Card
interface CardProps extends BaseProps {
  title: string
  description?: string
  image?: string
  link?: string
  icon?: string
}

function Card({ title, description, image, link, icon }: CardProps) {
  const content = (
    <div className="border rounded-xl overflow-hidden hover:shadow-lg transition-shadow bg-white h-full">
      {image && <img src={image} alt={title} className="w-full h-48 object-cover" />}
      <div className="p-6">
        {icon && <div className="text-4xl mb-4">{icon}</div>}
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        {description && <p className="text-gray-600 text-sm">{description}</p>}
      </div>
    </div>
  )
  return link ? (
    <a href={link} className="block">
      {content}
    </a>
  ) : (
    content
  )
}

// Container
interface ContainerProps extends BaseProps {
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  padding?: boolean
  background?: string
}

function Container({ maxWidth = 'lg', padding = true, background, children }: ContainerProps) {
  const maxWidthClasses = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-6xl',
    xl: 'max-w-7xl',
    full: 'max-w-full',
  }
  return (
    <div className={background ? 'py-8' : ''} style={background ? { backgroundColor: background } : undefined}>
      <div className={'mx-auto ' + maxWidthClasses[maxWidth] + (padding ? ' px-4 py-8' : '')}>{children}</div>
    </div>
  )
}

// Section with title
interface SectionProps extends BaseProps {
  title?: string
  subtitle?: string
  background?: 'white' | 'gray' | 'dark'
}

function Section({ title, subtitle, background = 'white', children }: SectionProps) {
  const bgClass = { white: 'bg-white', gray: 'bg-gray-50', dark: 'bg-gray-900 text-white' }[background]
  return (
    <section className={'py-16 ' + bgClass}>
      <div className="container mx-auto px-4">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>}
            {subtitle && (
              <p className={'text-lg max-w-2xl mx-auto ' + (background === 'dark' ? 'text-gray-300' : 'text-gray-600')}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  )
}

// Spacer
interface SpacerProps {
  height?: number
}

function Spacer({ height = 8 }: SpacerProps) {
  return <div style={{ height: height * 4 + 'px' }} />
}

// Feature Grid (for showcasing features/services)
interface FeatureGridProps extends BaseProps {
  features: Array<{
    icon?: string
    title: string
    description: string
  }>
  columns?: number
}

function FeatureGrid({ features, columns = 3 }: FeatureGridProps) {
  const colClass = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'
  return (
    <div className={'grid gap-8 ' + colClass}>
      {features.map((feature, idx) => (
        <div key={idx} className="text-center p-6 bg-white rounded-xl shadow-sm border">
          {feature.icon && <div className="text-4xl mb-4">{feature.icon}</div>}
          <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
          <p className="text-gray-600">{feature.description}</p>
        </div>
      ))}
    </div>
  )
}

// Testimonial
interface TestimonialProps extends BaseProps {
  quote: string
  author: string
  role?: string
  avatar?: string
  rating?: number
}

function Testimonial({ quote, author, role, avatar, rating }: TestimonialProps) {
  return (
    <blockquote className="bg-white p-8 rounded-xl shadow-sm border">
      {rating && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <svg
              key={i}
              style={{ width: '20px', height: '20px', color: i < rating ? '#facc15' : '#d1d5db' }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      )}
      <p className="text-lg italic mb-6 text-gray-700">"{quote}"</p>
      <footer className="flex items-center">
        {avatar ? (
          <img src={avatar} alt={author} className="w-12 h-12 rounded-full mr-4 object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full mr-4 bg-gray-200 flex items-center justify-center text-gray-500 font-semibold">
            {author.charAt(0)}
          </div>
        )}
        <div>
          <cite className="font-semibold not-italic">{author}</cite>
          {role && <p className="text-gray-500 text-sm">{role}</p>}
        </div>
      </footer>
    </blockquote>
  )
}

// Testimonial Slider
interface TestimonialSliderProps {
  testimonials: Array<{
    quote: string
    author: string
    role?: string
    avatar?: string
    rating?: number
  }>
}

function TestimonialSlider({ testimonials }: TestimonialSliderProps) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {testimonials.map((t, idx) => (
        <Testimonial key={idx} {...t} />
      ))}
    </div>
  )
}

// Contact Form
interface ContactFormProps {
  title?: string
  subtitle?: string
}

function ContactForm({ title, subtitle }: ContactFormProps) {
  return (
    <div className="max-w-lg mx-auto">
      {title && <h3 className="text-2xl font-bold mb-2 text-center">{title}</h3>}
      {subtitle && <p className="text-gray-600 mb-6 text-center">{subtitle}</p>}
      <form className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">First Name</label>
            <input
              type="text"
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Last Name</label>
            <input
              type="text"
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Doe"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            type="email"
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="john@example.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Phone</label>
          <input
            type="tel"
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder="+91 12345 67890"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
            rows={4}
            placeholder="How can we help you?"
          />
        </div>
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          Send Message
        </button>
      </form>
    </div>
  )
}

// Call to Action Section
interface CTAProps {
  title: string
  subtitle?: string
  buttonText?: string
  buttonLink?: string
  ctaText?: string
  ctaLink?: string
  secondaryCtaText?: string
  secondaryCtaLink?: string
  background?: 'primary' | 'dark' | 'gradient'
  backgroundColor?: string
}

function CTA({ title, subtitle, buttonText, buttonLink, ctaText, ctaLink, secondaryCtaText, secondaryCtaLink, background = 'primary', backgroundColor }: CTAProps) {
  const primaryText = ctaText || buttonText
  const primaryLink = ctaLink || buttonLink

  const bgStyle = backgroundColor
    ? { backgroundColor }
    : undefined

  const bgClass = backgroundColor
    ? ''
    : {
        primary: 'bg-blue-600',
        dark: 'bg-gray-900',
        gradient: 'bg-gradient-to-r from-blue-600 to-purple-600',
      }[background]

  return (
    <section className={'py-16 ' + bgClass} style={bgStyle}>
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{title}</h2>
        {subtitle && <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">{subtitle}</p>}
        <div className="flex flex-wrap gap-4 justify-center">
          {primaryText && primaryLink && (
            <a
              href={primaryLink}
              className="inline-block bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {primaryText}
            </a>
          )}
          {secondaryCtaText && secondaryCtaLink && (
            <a
              href={secondaryCtaLink}
              className="inline-block border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              {secondaryCtaText}
            </a>
          )}
        </div>
      </div>
    </section>
  )
}

// Stats Section
interface StatsProps {
  stats: Array<{
    value: string
    label: string
  }>
  title?: string
  backgroundColor?: string
}

function Stats({ stats, title, backgroundColor }: StatsProps) {
  const bgStyle = backgroundColor ? { backgroundColor } : undefined
  const isDark = backgroundColor && (backgroundColor.startsWith('#1') || backgroundColor.startsWith('#2') || backgroundColor.startsWith('#0'))

  return (
    <section className={'py-16 ' + (backgroundColor ? '' : 'bg-white')} style={bgStyle}>
      <div className="container mx-auto px-4">
        {title && (
          <h2 className={'text-3xl md:text-4xl font-bold text-center mb-12 ' + (isDark ? 'text-white' : 'text-gray-900')}>
            {title}
          </h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((stat, idx) => (
            <div key={idx}>
              <div className={'text-4xl md:text-5xl font-bold mb-2 ' + (isDark ? 'text-white' : 'text-blue-600')}>{stat.value}</div>
              <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// Team Section
interface TeamMemberProps {
  name: string
  role: string
  image?: string
  bio?: string
}

interface TeamProps {
  members: TeamMemberProps[]
}

function Team({ members }: TeamProps) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {members.map((member, idx) => (
        <div key={idx} className="text-center">
          {member.image ? (
            <img src={member.image} alt={member.name} className="w-32 h-32 rounded-full mx-auto mb-4 object-cover" />
          ) : (
            <div className="w-32 h-32 rounded-full mx-auto mb-4 bg-gray-200 flex items-center justify-center text-4xl text-gray-400">
              {member.name.charAt(0)}
            </div>
          )}
          <h3 className="font-semibold text-lg">{member.name}</h3>
          <p className="text-blue-600 mb-2">{member.role}</p>
          {member.bio && <p className="text-gray-600 text-sm">{member.bio}</p>}
        </div>
      ))}
    </div>
  )
}

// Service Card
interface ServiceCardProps {
  title: string
  description: string
  price?: string
  duration?: string
  image?: string
  icon?: string
}

function ServiceCard({ title, description, price, duration, image, icon }: ServiceCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
      {image && <img src={image} alt={title} className="w-full h-48 object-cover" />}
      <div className="p-6">
        {icon && <div className="text-4xl mb-4">{icon}</div>}
        <h3 className="font-semibold text-xl mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{description}</p>
        <div className="flex justify-between items-center">
          {price && <span className="font-bold text-lg">{price}</span>}
          {duration && <span className="text-gray-500 text-sm">{duration}</span>}
        </div>
      </div>
    </div>
  )
}

// Service Grid
interface ServiceGridProps {
  services: ServiceCardProps[]
}

function ServiceGrid({ services }: ServiceGridProps) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {services.map((service, idx) => (
        <ServiceCard key={idx} {...service} />
      ))}
    </div>
  )
}

// Price Table
interface PriceTableProps {
  items: Array<{
    name: string
    price: string
    description?: string
  }>
  title?: string
}

function PriceTable({ items, title }: PriceTableProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {title && (
        <div className="bg-gray-50 px-6 py-4 border-b">
          <h3 className="font-semibold text-lg">{title}</h3>
        </div>
      )}
      <div className="divide-y">
        {items.map((item, idx) => (
          <div key={idx} className="px-6 py-4 flex justify-between items-center">
            <div>
              <div className="font-medium">{item.name}</div>
              {item.description && <div className="text-sm text-gray-500">{item.description}</div>}
            </div>
            <div className="font-semibold text-lg">{item.price}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Divider
interface DividerProps {
  style?: 'line' | 'dots' | 'gradient'
}

function Divider({ style = 'line' }: DividerProps) {
  if (style === 'dots') {
    return (
      <div className="flex justify-center gap-2 py-8">
        <span className="w-2 h-2 rounded-full bg-gray-300"></span>
        <span className="w-2 h-2 rounded-full bg-gray-300"></span>
        <span className="w-2 h-2 rounded-full bg-gray-300"></span>
      </div>
    )
  }
  if (style === 'gradient') {
    return <div className="h-1 my-8 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
  }
  return <hr className="my-8 border-gray-200" />
}

// Two Column Layout
interface TwoColumnProps extends BaseProps {
  // New props format from pages.json
  imageUrl?: string
  imageAlt?: string
  title?: string
  content?: string
  ctaText?: string
  ctaLink?: string
  imagePosition?: 'left' | 'right'
  // Legacy props
  leftContent?: string
  rightContent?: string
  imageLeft?: string
  imageRight?: string
  reversed?: boolean
}

function TwoColumn({
  imageUrl, imageAlt, title, content, ctaText, ctaLink, imagePosition = 'left',
  leftContent, rightContent, imageLeft, imageRight, reversed,
  children
}: TwoColumnProps) {
  // Determine if image should be on left or right
  const _isImageLeft = imagePosition === 'left' || imageLeft || reversed === false
  const _isImageRight = imagePosition === 'right' || imageRight || reversed === true
  void _isImageLeft; void _isImageRight; // Suppress unused variable warnings

  // Use new format if imageUrl is provided
  if (imageUrl) {
    return (
      <div className="container mx-auto px-4">
        <div className={'grid md:grid-cols-2 gap-12 items-center'}>
          <div className={imagePosition === 'right' ? 'md:order-2' : ''}>
            <img src={imageUrl} alt={imageAlt || ''} className="w-full rounded-lg shadow-lg" />
          </div>
          <div className={imagePosition === 'right' ? 'md:order-1' : ''}>
            {title && <h2 className="text-3xl font-bold mb-4">{title}</h2>}
            {content && (
              <div className="prose prose-lg text-gray-600 mb-6 whitespace-pre-line">
                {content}
              </div>
            )}
            {ctaText && ctaLink && (
              <a
                href={ctaLink}
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {ctaText}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Legacy format
  return (
    <div className={'grid md:grid-cols-2 gap-12 items-center ' + (reversed ? 'direction-rtl' : '')}>
      <div className={reversed ? 'md:order-2' : ''}>
        {imageLeft && <img src={imageLeft} alt="" className="w-full rounded-lg" />}
        {leftContent && <div className="prose prose-lg" dangerouslySetInnerHTML={{ __html: leftContent }} />}
      </div>
      <div className={reversed ? 'md:order-1' : ''}>
        {imageRight && <img src={imageRight} alt="" className="w-full rounded-lg" />}
        {rightContent && <div className="prose prose-lg" dangerouslySetInnerHTML={{ __html: rightContent }} />}
      </div>
      {children}
    </div>
  )
}

// FAQ Accordion
interface FAQProps {
  questions?: Array<{
    question: string
    answer: string
  }>
  items?: Array<{
    question: string
    answer: string
  }>
  title?: string
}

function FAQ({ questions, items, title }: FAQProps) {
  const faqItems = items || questions || []

  return (
    <div className="container mx-auto px-4">
      {title && (
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">{title}</h2>
      )}
      <div className="max-w-3xl mx-auto divide-y border rounded-xl overflow-hidden bg-white">
        {faqItems.map((item, idx) => (
          <details key={idx} className="group">
            <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '16px 24px' }} className="hover:bg-gray-50">
              <span style={{ fontWeight: 500 }}>{item.question}</span>
              <svg
                style={{ width: '20px', height: '20px', color: '#6b7280', transition: 'transform 0.2s' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div style={{ padding: '0 24px 16px', color: '#4b5563' }}>{item.answer}</div>
          </details>
        ))}
      </div>
    </div>
  )
}

// Component Registry - maps component types to implementations
export const ComponentRegistry: Record<string, ComponentType<any>> = {
  hero: Hero,
  text_block: TextBlock,
  text: TextBlock,
  image: Image,
  grid: Grid,
  card: Card,
  container: Container,
  section: Section,
  spacer: Spacer,
  feature_grid: FeatureGrid,
  testimonial: Testimonial,
  testimonial_slider: TestimonialSlider,
  contact_form: ContactForm,
  cta: CTA,
  stats: Stats,
  team: Team,
  service_card: ServiceCard,
  service_grid: ServiceGrid,
  price_table: PriceTable,
  divider: Divider,
  two_column: TwoColumn,
  faq: FAQ,
}
